import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import TransMexLogo from "../../../assets/TransMexLogo-Final_CMYK.png";
import { GET } from "../../Generic/CrudRequest";
import i18n from "../../../i18n";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "rgba(255,255,255,0.85)",
    transition: "box-shadow 0.2s ease",
    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(7, 70, 142, 0.12)",
    },
  },
};

export default function ForgotPassword({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [requestError, setRequestError] = useState("");

  const currentLanguage = (i18n.language || "es").split("-")[0];
  const primary = theme.palette.primary.main;
  const nav = theme.palette.nav?.main ?? "#000922";

  const handleLanguageChange = (_, lang) => {
    if (!lang) return;
    localStorage.setItem("language", lang);
    i18n.changeLanguage(lang);
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setRequestError("");
    setSuccessMessage("");

    try {
      await GET(
        `${config.url}/api/User/forgot-password/${encodeURIComponent(email.trim())}`
      );
      setSuccessMessage(t("account.forgotPassword.sendSuccess"));
      setTimeout(() => navigate("/Account/Login"), 3500);
    } catch {
      setRequestError(t("account.forgotPassword.sendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setEmailError("");
    setRequestError("");
    setSuccessMessage("");

    if (!email.trim()) {
      setEmailError(t("account.forgotPassword.emailRequired"));
      return;
    }

    if (!EMAIL_PATTERN.test(email.trim())) {
      setEmailError(t("validation.emailFormat"));
      return;
    }

    handleForgotPassword();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${nav} 0%, ${primary} 50%, #1a8fd4 100%)`,
        p: 2,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={currentLanguage}
          onChange={handleLanguageChange}
          sx={{
            bgcolor: "rgba(255,255,255,0.14)",
            backdropFilter: "blur(12px)",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.22)",
            "& .MuiToggleButton-root": {
              color: "rgba(255,255,255,0.9)",
              border: 0,
              px: 2,
              py: 0.6,
              fontWeight: 600,
              fontSize: "0.8rem",
              textTransform: "none",
              "&.Mui-selected": {
                bgcolor: "#fff",
                color: primary,
                "&:hover": { bgcolor: "#fff" },
              },
            },
          }}
        >
          <ToggleButton value="es">ES</ToggleButton>
          <ToggleButton value="en">EN</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3.5, sm: 4.5 },
          borderRadius: 4,
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 24px 48px rgba(0, 9, 34, 0.25)",
        }}
      >
        <Box
          component="img"
          src={TransMexLogo}
          alt={t("navigation.logoAlt")}
          sx={{
            display: "block",
            mx: "auto",
            maxWidth: "100%",
            height: "auto",
            maxHeight: 80,
            objectFit: "contain",
            mb: 2.5,
          }}
        />

        <Typography
          variant="h5"
          align="center"
          fontWeight={700}
          color="primary"
          sx={{ mb: 1 }}
        >
          {t("account.forgotPassword.title")}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
          sx={{ mb: 2.5 }}
        >
          {t("account.forgotPassword.description")}
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}

        {requestError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {requestError}
          </Alert>
        )}

        <TextField
          margin="normal"
          variant="outlined"
          fullWidth
          id="email"
          label={t("account.forgotPassword.email")}
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          disabled={!!successMessage}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
            if (requestError) setRequestError("");
          }}
          error={!!emailError}
          helperText={emailError}
          sx={fieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailOutlinedIcon color="primary" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !!successMessage}
          sx={{
            mt: 3,
            py: 1.5,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "0 8px 24px rgba(7, 70, 142, 0.35)",
          }}
        >
          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} color="inherit" />
              <span>{t("account.forgotPassword.sending")}</span>
            </Stack>
          ) : (
            t("account.forgotPassword.send")
          )}
        </Button>

        <Typography align="center" sx={{ mt: 2.5 }}>
          <Link
            component={RouterLink}
            to="/Account/Login"
            variant="body2"
            underline="hover"
            fontWeight={600}
            sx={{
              color: primary,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            {t("account.forgotPassword.backToLogin")}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
